import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { breakpoint } from '../layout/styles'

interface ImageLightboxModalProps {
  images: { url: string; uid: string }[]
  initialIndex: number
  onClose: () => void
}

const ImageLightboxModal = ({ images, initialIndex, onClose }: ImageLightboxModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }, [images.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }, [images.length])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goToPrev()
      if (e.key === 'ArrowRight') goToNext()
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [onClose, goToPrev, goToNext])

  if (typeof document === 'undefined') return null

  const currentImage = images[currentIndex]

  return ReactDOM.createPortal(
    <Overlay role="dialog" aria-modal="true" onClick={onClose}>
      <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close image viewer">
          <img src="/images/close-light.svg" alt="" width={24} height={24} />
        </CloseButton>

        <ImageWrapper>
          {images.length > 1 && (
            <NavButton position="left" onClick={goToPrev} aria-label="Previous image">
              <img src="/images/prev-light.svg" alt="" width={20} height={20} />
            </NavButton>
          )}

          <ModalImage key={currentImage.uid} src={currentImage.url} alt={`Product image ${currentIndex + 1}`} />

          {images.length > 1 && (
            <NavButton position="right" onClick={goToNext} aria-label="Next image">
              <img src="/images/next-light.svg" alt="" width={20} height={20} />
            </NavButton>
          )}
        </ImageWrapper>

        {images.length > 1 && (
          <Counter>
            {currentIndex + 1} / {images.length}
          </Counter>
        )}
      </ModalContent>
    </Overlay>,
    document.body
  )
}

export default ImageLightboxModal

const Overlay = styled.div<any>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ModalContent = styled.div<any>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1000px;
  max-height: 90vh;
  width: 100%;
  padding: 0 16px;

  @media ${breakpoint.desktop} {
    padding: 0 64px;
  }
`

const ImageWrapper = styled.div<any>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 48px;
`

const ModalImage = styled.img<any>`
  max-height: 80vh;
  max-width: 100%;
  object-fit: contain;
  user-select: none;
  display: block;
`

const CloseButton = styled.button<any>`
  position: absolute;
  top: -40px;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  @media ${breakpoint.desktop} {
    right: 64px;
  }
`

const NavButton = styled.button<any>`
  position: absolute;
  ${(props: any) => props.position}: 0;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`

const Counter = styled.div<any>`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-top: 16px;
  font-family: 'Open Sans', sans-serif;
  letter-spacing: 0.05em;
`
